import { ChevronRight, FileText, Folder, FolderOpen, LogOut, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { TreeNode } from "../types";

const STORAGE_KEY = "yufo-reader-expanded";
const DEFAULT_EXPANDED = ["projects", "kb", "kb/creative", "kb/business", "kb/competition", "kb/engineering", "kb/production"];

function pageCount(node: TreeNode): number {
  return node.kind === "page" ? 1 : (node.children ?? []).reduce((sum, child) => sum + pageCount(child), 0);
}

function directoryPaths(node: TreeNode, parent = ""): string[] {
  if (node.kind === "page") return [];
  const path = parent ? `${parent}/${node.name}` : node.name;
  const children = (node.children ?? []).flatMap((child) => directoryPaths(child, path));
  return path ? [path, ...children] : children;
}

function activeDirectories(path: string): string[] {
  const parts = path.split("/");
  const result: string[] = [];
  for (let index = 1; index < parts.length; index++) result.push(parts.slice(0, index).join("/"));
  return result;
}

interface SidebarProps {
  tree: TreeNode;
  currentPath: string;
  open: boolean;
  mobile: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onSearch: () => void;
  onLogout: () => void;
}

export function Sidebar({ tree, currentPath, open, mobile, onClose, onNavigate, onSearch, onLogout }: SidebarProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored) as string[]) : new Set(DEFAULT_EXPANDED);
    } catch {
      return new Set(DEFAULT_EXPANDED);
    }
  });

  useEffect(() => {
    setExpanded((previous) => new Set([...previous, ...activeDirectories(currentPath)]));
  }, [currentPath]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
  }, [expanded]);

  const totalPages = useMemo(() => pageCount(tree), [tree]);
  const allDirectories = useMemo(() => directoryPaths(tree), [tree]);
  const allExpanded = allDirectories.length > 0 && allDirectories.every((path) => expanded.has(path));

  const toggle = (path: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const renderNode = (node: TreeNode, parent = "", depth = 0): React.ReactNode => {
    const nodePath = parent ? `${parent}/${node.name}` : node.name;
    if (node.kind === "page") {
      const active = node.path === currentPath;
      return (
        <li key={node.path}>
          <button
            type="button"
            className="tree-page"
            data-active={active || undefined}
            style={{ "--depth": depth } as React.CSSProperties}
            onClick={() => {
              onNavigate(node.path!);
              if (mobile) onClose();
            }}
            title={node.path}
          >
            <FileText size={14} aria-hidden="true" />
            <span>{node.title}</span>
            <small>{node.type}</small>
          </button>
        </li>
      );
    }

    const isOpen = expanded.has(nodePath) || nodePath === "";
    return (
      <li key={nodePath || "root"} className="tree-directory">
        {nodePath && (
          <button
            type="button"
            className="tree-folder"
            style={{ "--depth": depth } as React.CSSProperties}
            aria-expanded={isOpen}
            onClick={() => toggle(nodePath)}
          >
            <ChevronRight className="tree-chevron" size={14} aria-hidden="true" />
            {isOpen ? <FolderOpen size={15} /> : <Folder size={15} />}
            <span>{node.title}</span>
            <small>{pageCount(node)}</small>
          </button>
        )}
        {isOpen && (
          <ul>{(node.children ?? []).map((child) => renderNode(child, nodePath, nodePath ? depth + 1 : depth))}</ul>
        )}
      </li>
    );
  };

  return (
    <>
      {mobile && open && <button type="button" className="drawer-overlay" aria-label="关闭目录" onClick={onClose} />}
      <aside className="sidebar" data-open={open || undefined} aria-label="知识库目录">
        <div className="sidebar-head">
          <div>
            <span className="sidebar-kicker">YUFO KNOWLEDGE</span>
            <strong>{totalPages} pages · 完整目录</strong>
          </div>
          <div className="sidebar-head-actions">
            <button
              type="button"
              className="tree-expand-button"
              onClick={() => setExpanded(allExpanded ? new Set() : new Set(allDirectories))}
              aria-label={allExpanded ? "收起全部目录" : "展开全部目录"}
            >
              {allExpanded ? "全部收起" : "全部展开"}
            </button>
            {mobile && <button type="button" className="icon-button" onClick={onClose} aria-label="关闭目录"><X size={19} /></button>}
          </div>
        </div>
        <button type="button" className="sidebar-search" onClick={onSearch}>
          <Search size={16} />
          <span>搜索知识库</span>
          <kbd>⌘ K</kbd>
        </button>
        <nav className="tree-nav" aria-label="页面树">
          <ul>{(tree.children ?? []).map((node) => renderNode(node))}</ul>
        </nav>
        <div className="sidebar-foot">
          <span>Server Git 是唯一实时源</span>
          <button type="button" onClick={onLogout}><LogOut size={15} />退出</button>
        </div>
      </aside>
    </>
  );
}
